import { AlertCircle } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import styles from './Field.module.css';

// The wrapper every form control sits in.
//
// 06-qol §7: field help is an inline hint, NOT a tooltip requiring hover — hover
// does not exist on touch. Required fields are marked before the user submits.
// 06-qol §4: the error appears next to the field and states the actual limit
// ("Maximum 10 MB", not "File too large").

interface FieldProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly isRequired?: boolean;
  /** Marks the field explicitly optional, for a form where most fields are not. */
  readonly isOptional?: boolean;
  /**
   * A control on the label's own line, right-aligned — show/hide on a password,
   * a unit toggle beside an amount. It sits above the input rather than inside
   * it so it never competes with the text being typed, and so a long label and a
   * long action wrap instead of overlapping.
   */
  readonly action?: ReactNode;
  readonly children: (ids: FieldIds) => ReactNode;
}

export interface FieldIds {
  readonly inputId: string;
  readonly describedBy: string | undefined;
  readonly isInvalid: boolean;
}

export function Field({
  label,
  hint,
  error,
  isRequired = false,
  isOptional = false,
  action,
  children,
}: FieldProps): ReactNode {
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();

  const described = [hint === undefined ? '' : hintId, error === undefined ? '' : errorId]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={inputId}>
          {label}
          {isRequired && (
            <span className={styles.required} aria-label="required">
              *
            </span>
          )}
          {isOptional && <span className={styles.optional}>optional</span>}
        </label>
        {action}
      </div>

      {children({
        inputId,
        describedBy: described === '' ? undefined : described,
        isInvalid: error !== undefined,
      })}

      {hint !== undefined && error === undefined && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span id={errorId} className={styles.error} role="alert">
          <AlertCircle size={13} aria-hidden />
          {error}
        </span>
      )}
    </div>
  );
}

/** Two controls side by side — a currency beside an amount, a date beside a time. */
export function FieldRow({ children }: { children: ReactNode }): ReactNode {
  return <div className={styles.row}>{children}</div>;
}
